import React, { useState } from "react";
import s from "./Messages.module.scss";
import clsx from "clsx";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import ReusableTable from "../../../../../../global components/Table/Table";
import NoData from "../../../../../../global components/Table/NoData/NoData";
import Envelope from "../../../../../../global components/Icons/Envelope/Envelope";
import Paginate from "../../../../../../global components/Paginate/Pagination";
import SelectField from "../../../../../../global components/Forms/SelectField/SelectField";
import { useQuery } from "../../../../../../libs/common";
import { UseAuthToken } from "../../../../../../libs/api/userSession";
import {
  parseClientError,
  swrFetcher,
} from "../../../../../../libs/api/Endpoints";
import { PaginatedSentMessage } from "../../../../../../libs/Utils/Types/Message";
import useSWR from "swr";
import Unauthorised from "../../../../../../global components/Unauthorised";

function Messages() {
  const query = useQuery();
  const userId = query.get("userId");
  const token = UseAuthToken();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageItems, setPageItems] = useState(50);
  const [filter, setFilter] = useState<string>("sent");
  const navigate = useNavigate();
  const {
    data: userMessages,
    isLoading,
    error,
  } = useSWR<PaginatedSentMessage, any, any>(
    token
      ? [
          `api/admin/users/${userId}/messages`,
          token,
          {
            page: currentPage,
            limit: pageItems,
            ...(filter && { status: filter }),
          },
        ]
      : null,
    swrFetcher,
    {
      revalidateOnFocus: true, // Refetch when the window regains focus
      // Refetch every 10 seconds (10000 ms)
      shouldRetryOnError: false, // Disable retrying on errors
    },
  );
  const columns = [
    {
      title: "Message title",
      dataIndex: "messageTitle",
      key: "messageTitle",
      render: (messageTitle: string) => (
        <span className={clsx(s.cell, s.messageTitle)}>{messageTitle}</span>
      ),
    },
    {
      title: "Type",
      dataIndex: "mesageType",
      key: "mesageType",
      render: (mesageType: string) => (
        <span className={clsx(s.cell)}>{mesageType.toUpperCase()}</span>
      ),
    },

    {
      title: "Groups",
      dataIndex: "group",
      key: "group",
      render: (group: string) => <span className={clsx(s.cell)}>{group}</span>,
    },
    {
      title: "Contacts",
      dataIndex: "contacts",
      key: "contacts",
      render: (contacts: string) => (
        <span className={clsx(s.cell)}>{contacts}</span>
      ),
    },

    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date: string) => (
        <span className={s.cell}>
          {" "}
          {dayjs.utc(date).local().format("MMM D, YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <span className={clsx(s.status, s.statusCell, s[status.toLowerCase()])}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      ),
    },
    {
      title: "Open rate",
      dataIndex: "openRate",
      key: "openRate",
      render: (openRate: string) => (
        <span className={clsx(s.cell)}>{openRate}</span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (text: any, record: { key: string }) => (
        <h5
          className={s.view}
          onClick={() =>
            navigate(
              `/dashboard/users/profile/message-details?userId=${userId}&msgId=${record.key}`,
            )
          }
        >
          View
        </h5>
      ),
    },
  ];

  const transformedData = userMessages?.data.map((message) => ({
    key: message.id,
    messageTitle: message.title || "N/a",
    mesageType: message.type.replace("_", " ") || "N/a",
    group: message.noOfContactGroups || "N/A",
    contacts: message.totalRecipients || "N/a",
    creditUsed: message.totalCreditsUsed || "N/a",
    date: message.sentAt,
    status: message.status || "N/a",
    openRate: message.deliveryRate ? message.deliveryRate.toLocaleString() : 0,
    actions: "", // You can customize actions here as needed
  }));

  // Handle error state

  const handlePageChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageItems(size);
  };
  function handleFilter(value: string) {
    setFilter(value);
  }
 
  return (
    <div className={s.wrapper}>
      <div className={s.tableWrapper}>
        <div className={s.formWrapper}>
          <div className={s.params}>
            <SelectField
              bordered
              label={"Status:"}
              defaultValue={"sent"}
              wrapperClassname={s.SelectWrapper}
              className={clsx(s.selectInput)}
              onChange={(value: any) => handleFilter(value)}
              style={{ width: "100%", height: "100%" }}
              options={[
                { value: "", label: "All" }, // Option to reset the filter
                { value: "sent", label: "Sent" },

                { value: "scheduled", label: "Scheduled" },
              ]}
            />
          </div>
          <Paginate
            onChange={handlePageChange}
            total={userMessages?.pagination.totalItems || 0}
            pageSize={pageItems}
          />
        </div>
        <ReusableTable
          loading={isLoading}
          noDataComponent={
            error ? (
              <Unauthorised
                error={{
                  title: "Error!",
                  message:
                    parseClientError(error).message || "Something went wrong",
                }}
              />
            ) : (
              <NoData
                Message="No message yet"
                subMessage="Get started by sending a message"
                icon={<Envelope fill="#F0EFEE" height={60} width={80} />}
              />
            )
          }
          dataSource={transformedData || []}
          columns={columns}
          //rowSelection={rowSelection} // Include row selection prop
        />
      </div>
    </div>
  );
}

export default Messages;
